import * as React from "react";
import { NavigateOptions, URLSearchParamsInit, useSearchParams } from "react-router";
import { useSharedTransition } from "../context/SharedTransition";

/**
 * A wrapper around useSearchParams that automatically wraps updates in a shared transition.
 *
 * Use this when search param changes trigger Suspense boundaries (like data fetching). The transition prevents jarring
 * loading states by keeping the old UI visible while the new data loads.
 *
 * This hook uses the shared transition context, so the pending state is visible to all components via
 * `useSharedTransition()`. This allows coordinated loading states across your UI (e.g., dimming tables, showing
 * spinners, etc.).
 *
 * @example
 *   ```tsx
 *   // In your action hook:
 *   const [searchParams, setSearchParams] = useTransitionSearchParams();
 *
 *   const updatePage = useCallback((page: number) => {
 *     setSearchParams(prev => {
 *       prev.set('page', page.toString());
 *       return prev;
 *     });
 *   }, [setSearchParams]);
 *
 *   // In your component (to show loading state):
 *   const [isPending] = useSharedTransition();
 *   return <div className={isPending ? "opacity-50" : ""}>...</div>
 *   ```;
 */
export function useTransitionSearchParams() {
  const [searchParams, setSearchParamsBase] = useSearchParams();
  const [, startTransition] = useSharedTransition();

  // Wrap setSearchParams to always use transitions
  // startTransition is stable (from React.useTransition via context)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSearchParams = React.useCallback(
    (
      nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
      navigateOpts?: NavigateOptions,
    ) => {
      startTransition(() => {
        setSearchParamsBase(nextInit, navigateOpts);
      });
    },
    // startTransition is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParamsBase],
  );

  return [searchParams, setSearchParams] as const;
}
